import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, unwrap, uploadFile } from "../client";
import type { components } from "../generated/schema";
import { noErrorToast, type AppMutationMeta } from "../mutationMeta";
import type { ClientDetail } from "../types";

type S = components["schemas"];

export type ClientFilters = { q?: string; status?: "active" | "archived" | "all"; type?: string; page?: number; ordering?: "name" | "-updated_at" };

const keys = {
  all: (slug: string) => ["tenant", slug, "clients"] as const,
  detail: (slug: string, id: string) => ["tenant", slug, "clients", "detail", id] as const,
  types: (slug: string) => ["tenant", slug, "client-types"] as const,
};

export function useClients(slug: string, filters: ClientFilters, enabled = true) {
  return useQuery({
    queryKey: [...keys.all(slug), "list", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/clients/", { params: { path: { tenant_slug: slug }, query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useClient(slug: string, id: string) {
  return useQuery({
    queryKey: keys.detail(slug, id),
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/clients/{id}/", { params: { path: { tenant_slug: slug, id } } })),
  });
}

export function useClientTypes(slug: string) {
  return useQuery({
    queryKey: keys.types(slug),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/client-types/", { params: { path: { tenant_slug: slug } } })),
    staleTime: 60_000,
  });
}

/** Generated request types: creation requires the fields the API requires; updates are partial. */
export type ClientWrite = S["PatchedClientWriteRequest"];
export type ClientCreate = S["ClientWriteRequest"];

export function useCreateClient(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (body: ClientCreate) => unwrap(api.POST("/api/t/{tenant_slug}/clients/", { params: { path: { tenant_slug: slug } }, body })),
    onSuccess: (client) => {
      qc.invalidateQueries({ queryKey: keys.all(slug) });
      qc.setQueryData(keys.detail(slug, client.id), client);
    },
  });
}

/** After any change to one client: its detail (replaced when the answer is the full client), the list and memberships. */
function useClientChanged(slug: string, clientId: string) {
  const qc = useQueryClient();
  return (client?: ClientDetail) => {
    if (client) qc.setQueryData(keys.detail(slug, clientId), client);
    else qc.invalidateQueries({ queryKey: keys.detail(slug, clientId) });
    qc.invalidateQueries({ queryKey: [...keys.all(slug), "list"] });
    qc.invalidateQueries({ queryKey: ["tenant", slug, "memberships"] });
  };
}

/** Mutations of the client itself answer with the full client. */
function useClientDetailMutation<V>(slug: string, clientId: string, fn: (vars: V) => Promise<ClientDetail>, meta: AppMutationMeta = noErrorToast) {
  const changed = useClientChanged(slug, clientId);
  return useMutation({ meta, mutationFn: fn, onSuccess: (client) => changed(client) });
}

/** Mutations of contacts, locations, links and access answer with the child only, so the client is refetched. */
function useClientChildMutation<V>(slug: string, clientId: string, fn: (vars: V) => Promise<unknown>) {
  const changed = useClientChanged(slug, clientId);
  return useMutation({ meta: noErrorToast, mutationFn: fn, onSuccess: () => changed() });
}

export function useUpdateClient(slug: string, id: string) {
  return useClientDetailMutation(slug, id, (body: ClientWrite) =>
    unwrap(api.PATCH("/api/t/{tenant_slug}/clients/{id}/", { params: { path: { tenant_slug: slug, id } }, body })),
  );
}

/** Restore shows the default error toast; pass `noErrorToast` where a ConfirmDialog renders the error (archive). */
export function useArchiveClient(slug: string, id: string, meta: AppMutationMeta = {}) {
  return useClientDetailMutation(
    slug,
    id,
    (archived: boolean) =>
      archived
        ? unwrap(api.POST("/api/t/{tenant_slug}/clients/{id}/archive/", { params: { path: { tenant_slug: slug, id } } }))
        : unwrap(api.POST("/api/t/{tenant_slug}/clients/{id}/restore/", { params: { path: { tenant_slug: slug, id } } })),
    meta,
  );
}

export function useClientLogo(slug: string, id: string) {
  return useClientDetailMutation(slug, id, (file: File | null) =>
    uploadFile<ClientDetail>(`/api/t/${slug}/clients/${id}/logo/`, file, file ? "POST" : "DELETE"),
  );
}

export type ContactWrite = S["PatchedClientContactWriteRequest"];

export function useSaveContact(slug: string, clientId: string) {
  return useClientChildMutation(slug, clientId, ({ id, body }: { id?: string; body: ContactWrite }) => {
    const path = { tenant_slug: slug, client_pk: clientId };
    return id
      ? unwrap(api.PATCH("/api/t/{tenant_slug}/clients/{client_pk}/contacts/{id}/", { params: { path: { ...path, id } }, body }))
      : unwrap(api.POST("/api/t/{tenant_slug}/clients/{client_pk}/contacts/", { params: { path }, body: { ...body, first_name: body.first_name ?? "" } }));
  });
}

export function useDeleteContact(slug: string, clientId: string) {
  return useClientChildMutation(slug, clientId, (id: string) =>
    unwrap(
      api.DELETE("/api/t/{tenant_slug}/clients/{client_pk}/contacts/{id}/", {
        params: { path: { tenant_slug: slug, client_pk: clientId, id } },
      }),
    ),
  );
}

export type LocationWrite = S["PatchedClientLocationWriteRequest"];

export function useSaveLocation(slug: string, clientId: string) {
  return useClientChildMutation(slug, clientId, ({ id, body }: { id?: string; body: LocationWrite }) => {
    const path = { tenant_slug: slug, client_pk: clientId };
    return id
      ? unwrap(api.PATCH("/api/t/{tenant_slug}/clients/{client_pk}/locations/{id}/", { params: { path: { ...path, id } }, body }))
      : unwrap(
          api.POST("/api/t/{tenant_slug}/clients/{client_pk}/locations/", {
            params: { path },
            body: { ...body, line1: body.line1 ?? "", city: body.city ?? "" },
          }),
        );
  });
}

export function useDeleteLocation(slug: string, clientId: string) {
  return useClientChildMutation(slug, clientId, (id: string) =>
    unwrap(
      api.DELETE("/api/t/{tenant_slug}/clients/{client_pk}/locations/{id}/", {
        params: { path: { tenant_slug: slug, client_pk: clientId, id } },
      }),
    ),
  );
}

export function useSaveLink(slug: string, clientId: string) {
  return useClientChildMutation(slug, clientId, ({ id, body }: { id?: string; body: S["PatchedClientLinkWriteRequest"] }) => {
    const path = { tenant_slug: slug, client_pk: clientId };
    return id
      ? unwrap(api.PATCH("/api/t/{tenant_slug}/clients/{client_pk}/links/{id}/", { params: { path: { ...path, id } }, body }))
      : unwrap(
          api.POST("/api/t/{tenant_slug}/clients/{client_pk}/links/", {
            params: { path },
            body: { label: body.label ?? "", url: body.url ?? "" },
          }),
        );
  });
}

export function useDeleteLink(slug: string, clientId: string) {
  return useClientChildMutation(slug, clientId, (id: string) =>
    unwrap(
      api.DELETE("/api/t/{tenant_slug}/clients/{client_pk}/links/{id}/", {
        params: { path: { tenant_slug: slug, client_pk: clientId, id } },
      }),
    ),
  );
}

export function useGrantClientAccess(slug: string, clientId: string) {
  return useClientChildMutation(slug, clientId, (body: S["ClientAccessWriteRequest"]) =>
    unwrap(api.POST("/api/t/{tenant_slug}/clients/{client_pk}/access/", { params: { path: { tenant_slug: slug, client_pk: clientId } }, body })),
  );
}

export function useRevokeClientAccess(slug: string, clientId: string) {
  return useClientChildMutation(slug, clientId, (id: string) =>
    unwrap(
      api.POST("/api/t/{tenant_slug}/clients/{client_pk}/access/{id}/revoke/", {
        params: { path: { tenant_slug: slug, client_pk: clientId, id } },
      }),
    ),
  );
}

export function useSaveClientType(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: ({ id, body }: { id?: string; body: S["PatchedClientTypeWriteRequest"] }) =>
      id
        ? unwrap(api.PATCH("/api/t/{tenant_slug}/client-types/{id}/", { params: { path: { tenant_slug: slug, id } }, body }))
        : unwrap(
            api.POST("/api/t/{tenant_slug}/client-types/", {
              params: { path: { tenant_slug: slug } },
              body: { ...body, key: body.key ?? "", name: body.name ?? "" },
            }),
          ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.types(slug) });
      // Lists and details embed the type's name and status.
      qc.invalidateQueries({ queryKey: keys.all(slug) });
    },
  });
}
